import { ref, computed } from 'vue'
import { useTerritorioStore } from '@/store/territorio'
import { useApi } from '@/composables/useApi'

// Migra líneas del formato viejo (paradas a nivel raíz) al nuevo (ida/vuelta)
const normalizeLinea = (l) => {
  if (l.ida) return l
  return {
    id: l.id,
    numero: l.numero,
    color: l.color || '#3388ff',
    horario: l.horario || { ida: { inicio: '', fin: '' }, vuelta: { inicio: '', fin: '' } },
    ida: { paradas: l.paradas || [], recorrido: l.recorrido || [] },
    vuelta: { paradas: [], recorrido: [] },
  }
}

export function useTransporte() {
  const terr = useTerritorioStore()
  const api = useApi()

  const lineaActiva = ref(null)
  const sentidoActivo = ref('ida')
  const modoEdicion = ref('paradas') // 'paradas' | 'recorrido'
  const guardando = ref(false)

  const lineas = computed(() => {
    if (!Array.isArray(terr.transporte)) return []
    return terr.transporte.map(normalizeLinea)
  })

  const lineaActivaData = computed(() =>
    lineas.value.find((l) => l.id === lineaActiva.value) ?? null,
  )

  const _getLinea = (id) => terr.transporte.find((l) => l.id === id)

  const agregarLinea = (numero, color = '#3388ff') => {
    if (!Array.isArray(terr.transporte)) terr.$patch({ transporte: [] })
    const nueva = {
      id: crypto.randomUUID(),
      numero,
      color,
      horario: { ida: { inicio: '', fin: '' }, vuelta: { inicio: '', fin: '' } },
      ida: { paradas: [], recorrido: [] },
      vuelta: { paradas: [], recorrido: [] },
    }
    terr.transporte.push(nueva)
    lineaActiva.value = nueva.id
    sentidoActivo.value = 'ida'
    modoEdicion.value = 'paradas'
  }

  const eliminarLinea = (id) => {
    terr.$patch({ transporte: terr.transporte.filter((l) => l.id !== id) })
    if (lineaActiva.value === id) lineaActiva.value = null
  }

  const seleccionarLinea = (id) => {
    lineaActiva.value = id
  }

  const agregarParada = (lineaId, sentido, latlng) => {
    const linea = _getLinea(lineaId)
    if (!linea) return
    if (!linea[sentido]) linea[sentido] = { paradas: [], recorrido: [] }
    linea[sentido].paradas.push({ id: crypto.randomUUID(), latlng, nombre: '' })
  }

  const eliminarParada = (lineaId, sentido, idx) => {
    const linea = _getLinea(lineaId)
    if (!linea?.[sentido]?.paradas) return
    linea[sentido].paradas.splice(idx, 1)
  }

  const agregarPuntoRecorrido = (lineaId, sentido, latlng) => {
    const linea = _getLinea(lineaId)
    if (!linea) return
    if (!linea[sentido]) linea[sentido] = { paradas: [], recorrido: [] }
    linea[sentido].recorrido.push([latlng.lat, latlng.lng])
  }

  const limpiarRecorrido = (lineaId, sentido) => {
    const linea = _getLinea(lineaId)
    if (!linea?.[sentido]) return
    linea[sentido].recorrido = []
  }

  const deshacerUltimoPunto = (lineaId, sentido) => {
    const linea = _getLinea(lineaId)
    if (!linea?.[sentido]?.recorrido?.length) return
    linea[sentido].recorrido.pop()
  }

  const actualizarHorario = (lineaId, sentido, campo, valor) => {
    const linea = _getLinea(lineaId)
    if (!linea) return
    if (!linea.horario) linea.horario = { ida: { inicio: '', fin: '' }, vuelta: { inicio: '', fin: '' } }
    if (!linea.horario[sentido]) linea.horario[sentido] = { inicio: '', fin: '' }
    linea.horario[sentido][campo] = valor
  }

  const guardar = async () => {
    guardando.value = true
    try {
      await api.put(`/api/territories/${terr.uuid}`, terr.getData())
    } finally {
      guardando.value = false
    }
  }

  const setSentido = (s) => { sentidoActivo.value = s }
  const setModo = (m) => { modoEdicion.value = m }

  return {
    lineas,
    lineaActiva,
    sentidoActivo,
    modoEdicion,
    lineaActivaData,
    guardando,
    agregarLinea,
    eliminarLinea,
    seleccionarLinea,
    agregarParada,
    eliminarParada,
    agregarPuntoRecorrido,
    limpiarRecorrido,
    deshacerUltimoPunto,
    actualizarHorario,
    setSentido,
    setModo,
    guardar,
  }
}
